/**
 * Exact bigint port of the on-chain resolution-threshold staircase:
 * `contracts/markets/programs/markets/src/state/resolution_threshold.rs`.
 *
 * Floor at creation is `ceil(PPB_ONE / n_outcomes) + floor_margin_ppb`, stored
 * on the market as `resolution_threshold_floor_ppb`. Effective threshold at
 * time `now` is the initial threshold until `trading_started + delay`, then it
 * drops by a fixed amount at each configured interval until reaching the floor.
 */

import type { ResolutionThresholdDecayPolicy } from '../conviction/interfaces.js';
import { CONVICTION_SCALE_PPB } from './conviction.js';

export type { ResolutionThresholdDecayPolicy } from '../conviction/interfaces.js';

/** Matches `PPB_ONE` / D9. */
export const RESOLUTION_THRESHOLD_PPB_ONE: bigint = CONVICTION_SCALE_PPB;
/** Matches `MIN_OUTCOMES`. */
export const RESOLUTION_THRESHOLD_MIN_OUTCOMES: bigint = 2n;
/** Matches `MAX_OUTCOMES`. */
export const RESOLUTION_THRESHOLD_MAX_OUTCOMES: bigint = 10n;
/** Matches the largest exactly indexed delay/interval the contract accepts. */
export const MAX_EXACT_INDEXED_INTEGER: bigint = 9_007_199_254_740_991n;
/** Matches `MIN_RESOLUTION_THRESHOLD_DROP_PPB`. */
export const MIN_RESOLUTION_THRESHOLD_DROP_PPB: bigint = 10_000_000n;

const U64_MAX: bigint = (1n << 64n) - 1n;

export function isResolutionThresholdDecayPolicy(
  value: bigint | ResolutionThresholdDecayPolicy,
): value is ResolutionThresholdDecayPolicy {
  return typeof value !== 'bigint';
}

/**
 * `resolution_threshold_floor_ppb(n, margin)`:
 * `ceil(1e9 / n_outcomes) + margin` via `(PPB_ONE + n - 1) / n + margin`.
 */
export function resolutionThresholdFloorPpb(
  activeOutcomeCount: bigint,
  floorMarginPpb: bigint,
): bigint {
  if (
    activeOutcomeCount < RESOLUTION_THRESHOLD_MIN_OUTCOMES ||
    activeOutcomeCount > RESOLUTION_THRESHOLD_MAX_OUTCOMES
  ) {
    throw new Error('invalid outcome count');
  }
  const margin = requireU64(floorMarginPpb, 'floor_margin_ppb');
  const count = activeOutcomeCount;
  const numerator = requireU64(RESOLUTION_THRESHOLD_PPB_ONE + (count - 1n), 'neutral_share');
  const neutralShare = numerator / count;
  return requireU64(neutralShare + margin, 'resolution_threshold_floor_ppb');
}

/**
 * `effective_resolution_threshold_ppb` — bit-exact with the contract.
 */
export function effectiveResolutionThresholdPpb(
  initialThresholdPpb: bigint,
  thresholdFloorPpb: bigint,
  tradingStartedTs: bigint,
  decayDelaySecs: bigint,
  thresholdDropPpb: bigint,
  thresholdDropIntervalSecs: bigint,
  nowTs: bigint,
): bigint {
  const initial = requireU64(initialThresholdPpb, 'initial_threshold_ppb');
  const floor = requireU64(thresholdFloorPpb, 'threshold_floor_ppb');
  if (floor > initial || initial > RESOLUTION_THRESHOLD_PPB_ONE) {
    throw new Error('invalid resolution threshold policy');
  }
  const delay = requireU64(decayDelaySecs, 'decay_delay_secs');
  const drop = requireU64(thresholdDropPpb, 'threshold_drop_ppb');
  const interval = requireU64(thresholdDropIntervalSecs, 'threshold_drop_interval_secs');
  if (
    delay > MAX_EXACT_INDEXED_INTEGER ||
    drop < MIN_RESOLUTION_THRESHOLD_DROP_PPB ||
    interval <= 0n ||
    interval > MAX_EXACT_INDEXED_INTEGER
  ) {
    throw new Error('invalid resolution threshold policy');
  }
  const age = tradingAgeSecs(tradingStartedTs, nowTs);
  const elapsedDropCount = saturatingSub(age, delay) / interval;
  const totalDropCount = resolutionThresholdDropCount(initial, floor, drop);
  if (elapsedDropCount >= totalDropCount) return floor;
  return requireU64(initial - drop * elapsedDropCount, 'resolution_threshold_ppb');
}

export function effectiveResolutionThresholdPpbFromPolicy(
  policy: ResolutionThresholdDecayPolicy,
  nowTs: bigint,
): bigint {
  return effectiveResolutionThresholdPpb(
    policy.initialThresholdPpb,
    policy.thresholdFloorPpb,
    policy.tradingStartedTs,
    policy.decayDelaySecs,
    policy.dropPpb,
    policy.dropIntervalSecs,
    nowTs,
  );
}

export function resolutionThresholdDropCount(
  initialThresholdPpb: bigint,
  thresholdFloorPpb: bigint,
  thresholdDropPpb: bigint,
): bigint {
  const range = initialThresholdPpb - thresholdFloorPpb;
  if (
    range < 0n ||
    initialThresholdPpb > RESOLUTION_THRESHOLD_PPB_ONE ||
    thresholdDropPpb < MIN_RESOLUTION_THRESHOLD_DROP_PPB
  ) {
    throw new Error('invalid resolution threshold policy');
  }
  return range === 0n ? 0n : (range + thresholdDropPpb - 1n) / thresholdDropPpb;
}

function tradingAgeSecs(tradingStartedTs: bigint, nowTs: bigint): bigint {
  if (nowTs <= tradingStartedTs) return 0n;
  return requireU64(nowTs - tradingStartedTs, 'trading_age_secs');
}

function saturatingSub(value: bigint, subtrahend: bigint): bigint {
  return value > subtrahend ? value - subtrahend : 0n;
}

function requireU64(value: bigint, field: string): bigint {
  if (value < 0n || value > U64_MAX) {
    throw new Error(`${field} exceeds u64`);
  }
  return value;
}
